import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Redirect, withRouter, Route } from 'react-router-dom';
import { Translate } from 'core/translate';
import { SvgIcon } from 'core/icons';
import TrimCompare from '../featuresCompare/TrimCompare';
import FeaturesTrimsSelect from './FeaturesTrimsSelect';
import CapabilitiesCompare from '../featuresCompare/CapabilitiesCompare';
import FeaturesCapabilitiesSelect from './FeaturesCapabilitiesSelect';
import FeaturesHeader from './FeaturesHeader';
import { withModelInfo } from '../../common/ModelInfoContext';
import fetchCapabilities from '../../api/fetchCapabilities';
import fetchModelMatrix from '../../api/fetchModelMatrix';
import fetchFeatures from '../../api/fetchFeatures';

const Loading = () => (
  <div
    style={{
      display: 'flex',
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
    <SvgIcon name="spinner" style={{ width: 40, height: 40 }} />
  </div>
);

const FeaturesLayout = ({ header, content }) => (
  <div className="features">
    <div>{header}</div>
    <div className="features__content">{content}</div>
  </div>
);

class Features extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isTrimFeaturesLoaded: false,
      isCapabilitiesLoaded: false,
      isMatrixLoaded: false,
      trimFeatures: null,
      capabilities: null,
      matrix: null
    };
  }

  componentDidMount() {
    const { modelInfo, config, disableCapabilities, postalCode } = this.props;
    fetchFeatures({ ...modelInfo, postalCode }).then(trimFeatures => {
      this.setState({
        trimFeatures,
        isTrimFeaturesLoaded: true
      });
    });
    if (!disableCapabilities) {
      fetchCapabilities(modelInfo).then(capabilities => {
        this.setState({
          capabilities,
          isCapabilitiesLoaded: true
        });
      });
      fetchModelMatrix({ ...modelInfo, config }).then(matrix => {
        this.setState({
          matrix,
          isMatrixLoaded: true
        });
      });
    }
  }

  render() {
    const {
      history,
      match,
      returnHref,
      returnUrl,
      returnText,
      config,
      disclaimer,
      disableCapabilities,
      classicModel
    } = this.props;
    const {
      trimFeatures,
      isTrimFeaturesLoaded,
      capabilities,
      isCapabilitiesLoaded,
      matrix,
      isMatrixLoaded
    } = this.state;

    const baseUrl = match.url;
    const trimsPath = `${baseUrl}/trims`;
    const capabilitiesPath = `${baseUrl}/capabilities`;

    const onSectionsChange = ({ match, location }) => sections => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('section');
      for (const section of sections) {
        searchParams.append('section', section);
      }
      searchParams.sort();
      history.replace(`${match.url}?${searchParams.toString()}`);
    };

    return (
      <Switch>
        <Route
          exact
          path={baseUrl}
          render={({ location, match }) => (
            <Redirect to={`${match.url}/trims/table${location.search}`} />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/trims`}
          render={({ location, match }) => (
            <Redirect to={`${match.url}/table${location.search}`} />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/capabilities`}
          render={({ location, match }) => (
            <Redirect to={`${match.url}/table${location.search}`} />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/trims/table`}
          render={({ location, match }) => (
            <FeaturesLayout
              header={
                <FeaturesHeader
                  backText={returnText}
                  headerText={
                    <Translate value="component.trim.compare.trims" />
                  }
                  returnUrl={returnUrl}
                  returnHref={returnHref}
                  trimsPath={trimsPath}
                  capabilitiesPath={capabilitiesPath}
                  disableCapabilities={disableCapabilities}
                />
              }
              content={
                isTrimFeaturesLoaded ? (
                  <TrimCompare
                    match={match}
                    location={location}
                    config={config}
                    features={trimFeatures}
                    disclaimer={disclaimer}
                    lockDefaultColumn={true}
                    onSelect={index => {
                      history.push(
                        `${baseUrl}/trims/select/${index}${location.search}`
                      );
                    }}
                    onSectionsChange={onSectionsChange({ match, location })}
                  />
                ) : (
                  <Loading />
                )
              }
            />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/trims/select/:index`}
          render={({ match, location }) => (
            <FeaturesLayout
              header={
                <FeaturesHeader
                  backText={
                    <Translate value="component.trim.compare.return.to.trim.compare" />
                  }
                  headerText={
                    <Translate value="component.trim.compare.add.vehicle" />
                  }
                  returnUrl={`${baseUrl}/trims${location.search}`}
                  trimsPath={trimsPath}
                  capabilitiesPath={capabilitiesPath}
                  disableCapabilities={disableCapabilities}
                />
              }
              content={
                isTrimFeaturesLoaded ? (
                  <FeaturesTrimsSelect
                    options={trimFeatures.options}
                    config={config}
                    onCancel={() => {
                      history.push(`${baseUrl}/trims/table${location.search}`);
                    }}
                    onSubmit={({ styleId }) => {
                      const searchParams = new URLSearchParams(location.search);
                      switch (match.params.index) {
                        case '0':
                          searchParams.set('styleOne', styleId);
                          break;
                        case '1':
                          searchParams.set('styleTwo', styleId);
                          break;
                        case '2':
                          searchParams.set('styleThree', styleId);

                          break;
                      }
                      history.push(
                        `${baseUrl}/trims/table?${searchParams.toString()}`
                      );
                    }}
                  />
                ) : (
                  <Loading />
                )
              }
            />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/capabilities/table`}
          render={({ location, match }) => (
            <FeaturesLayout
              header={
                <FeaturesHeader
                  backText={returnText}
                  headerText={
                    <Translate value="component.trim.compare.capabilities" />
                  }
                  returnUrl={returnUrl}
                  returnHref={returnHref}
                  trimsPath={trimsPath}
                  capabilitiesPath={capabilitiesPath}
                  disableCapabilities={disableCapabilities}
                />
              }
              content={
                isTrimFeaturesLoaded && isCapabilitiesLoaded ? (
                  <CapabilitiesCompare
                    location={location}
                    match={match}
                    config={config}
                    features={capabilities}
                    trimFeatures={trimFeatures}
                    disclaimer={disclaimer}
                    lockDefaultColumn={true}
                    onSelect={index => {
                      history.push(
                        `${baseUrl}/capabilities/select/${index}${
                          location.search
                        }`
                      );
                    }}
                    onSectionsChange={onSectionsChange({ match, location })}
                  />
                ) : (
                  <Loading />
                )
              }
            />
          )}
        />
        <Route
          exact
          path={`${baseUrl}/capabilities/select/:index`}
          render={({ match, location }) => (
            <FeaturesLayout
              header={
                <FeaturesHeader
                  backText={
                    <Translate value="component.trim.compare.return.to.capabilities.compare" />
                  }
                  headerText={
                    <Translate value="component.trim.compare.add.vehicle" />
                  }
                  returnUrl={`${baseUrl}/capabilities${location.search}`}
                  trimsPath={trimsPath}
                  capabilitiesPath={capabilitiesPath}
                  disableCapabilities={disableCapabilities}
                />
              }
              content={
                isMatrixLoaded ? (
                  <FeaturesCapabilitiesSelect
                    matrix={matrix}
                    config={config}
                    classicModel={classicModel}
                    onCancel={() => {
                      history.push(
                        `${baseUrl}/capabilities/table${location.search}`
                      );
                    }}
                    onSubmit={({ driveType, bodyTypeId, engine }) => {
                      const searchParams = new URLSearchParams(location.search);
                      switch (match.params.index) {
                        case '0':
                          searchParams.set('driveTypeOne', driveType);
                          searchParams.set('bodyTypeOne', bodyTypeId);
                          searchParams.set('engineOne', engine);
                          break;
                        case '1':
                          searchParams.set('driveTypeTwo', driveType);
                          searchParams.set('bodyTypeTwo', bodyTypeId);
                          searchParams.set('engineTwo', engine);
                          break;
                        case '2':
                          searchParams.set('driveTypeThree', driveType);
                          searchParams.set('bodyTypeThree', bodyTypeId);
                          searchParams.set('engineThree', engine);
                          break;
                      }

                      history.push(
                        `${baseUrl}/capabilities/table?${searchParams.toString()}`
                      );
                    }}
                  />
                ) : (
                  <Loading />
                )
              }
            />
          )}
        />
        <Route render={() => <Redirect to={baseUrl} />} />
      </Switch>
    );
  }
}

Features.propTypes = {
  returnText: PropTypes.node.isRequired,
  returnUrl: PropTypes.string,
  returnHref: PropTypes.string,
  config: PropTypes.object.isRequired,
  disclaimer: PropTypes.string,
  disableCapabilities: PropTypes.bool,
  classicModel: PropTypes.bool.isRequired,
  postalCode: PropTypes.string.isRequired
};

export default withRouter(withModelInfo(Features));
